import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Search, Plus, User, Loader2, FileText } from 'lucide-react'
import { api } from '../lib/api'

const NOTE_TYPES = [
  { value: 'op_note', label: 'Op Note' },
  { value: 'office', label: 'Office Visit' },
  { value: 'consult', label: 'Consult' },
  { value: 'inpatient', label: 'Rounding' },
  { value: 'procedure', label: 'Procedure' },
  { value: 'telehealth', label: 'Telehealth' },
]

interface Patient {
  id: string
  first_name?: string
  last_name?: string
  name?: string
  date_of_birth?: string
}

function patientLabel(p: Patient) {
  if (p.last_name && p.first_name) return `${p.last_name}, ${p.first_name}`
  if (p.last_name) return p.last_name
  if (p.first_name) return p.first_name
  return p.name || 'Unknown'
}

export function NewEncounterPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  // Patient state
  const [patients, setPatients] = useState<Patient[]>([])
  const [patientSearch, setPatientSearch] = useState('')
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null)
  const [showPatientDropdown, setShowPatientDropdown] = useState(false)
  const [loadingPatients, setLoadingPatients] = useState(false)

  // New patient inline
  const [showNewPatient, setShowNewPatient] = useState(false)
  const [newLastName, setNewLastName] = useState('')
  const [newFirstName, setNewFirstName] = useState('')
  const [newDob, setNewDob] = useState('')
  const [creatingPatient, setCreatingPatient] = useState(false)

  // Note config
  const [noteType, setNoteType] = useState('op_note')
  const [isNewPatient, setIsNewPatient] = useState(false)
  const [tier, setTier] = useState<string>('pro')  // optimistic; corrected on load

  useEffect(() => {
    api.getBillingStatus().then((b: any) => {
      const tr = b.tier || 'core'
      setTier(tr)
      if (tr !== 'pro') setNoteType(prev => prev === 'op_note' ? 'office' : prev)
    }).catch(() => {})
  }, [])
  const [content, setContent] = useState('')
  const [procedureName, setProcedureName] = useState('')

  // Generating
  const [generating, setGenerating] = useState(false)

  useEffect(() => {
    loadPatients()
  }, [])

  async function loadPatients() {
    setLoadingPatients(true)
    try {
      const data = await api.listPatients()
      const list = data.patients || data
      setPatients(list)
      // Pre-select from schedule capture (?patientId=...&patient=Name).
      const qpId = searchParams.get('patientId')
      const qpName = searchParams.get('patient')
      if (qpId) {
        const match = (list || []).find((p: Patient) => p.id === qpId)
        if (match) { setSelectedPatient(match); }
        else if (qpName) { setPatientSearch(qpName) }
      } else if (qpName) {
        setPatientSearch(qpName)
      }
    } catch {}
    setLoadingPatients(false)
  }

  const filteredPatients = patientSearch.trim()
    ? patients.filter(p => {
        const q = patientSearch.toLowerCase()
        return (
          (p.first_name?.toLowerCase().includes(q)) ||
          (p.last_name?.toLowerCase().includes(q)) ||
          (p.name?.toLowerCase().includes(q))
        )
      })
    : patients

  async function handleCreatePatient() {
    if (!newLastName.trim()) return
    setCreatingPatient(true)
    try {
      const patient = await api.createPatient({
        first_name: newFirstName.trim(),
        last_name: newLastName.trim(),
        dob: newDob || undefined,
      })
      setPatients(prev => [patient, ...prev])
      setSelectedPatient(patient)
      setShowNewPatient(false)
      setNewLastName('')
      setNewFirstName('')
      setNewDob('')
    } catch (err: any) {
      alert(err.message || 'Failed to create patient')
    }
    setCreatingPatient(false)
  }

  async function handleGenerate() {
    if (!content.trim()) return
    setGenerating(true)
    try {
      // Create encounter
      const enc = await api.createEncounter({
        encounter_type: noteType,
        capture_mode: 'ambient',
        patient_id: selectedPatient?.id,
        procedure_name: procedureName.trim() || undefined,
        is_new_patient: isNewPatient || undefined,
      })

      // Update transcript directly so the pipeline has content
      // We use the encounter's transcript field
      await api.updateEncounterTranscript(enc.id, content.trim())

      // Navigate to note page — auto-generates when transcript exists
      navigate(`/encounter/${enc.id}/note`)
    } catch (err: any) {
      alert(err.message || 'Failed to create visit')
      setGenerating(false)
    }
  }

  return (
    <div className="max-w-2xl mx-auto pb-24 md:pb-8">
      <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-1">New Visit</h1>
      <p className="text-gray-500 dark:text-gray-400 text-sm mb-6">
        Create a note from typed or pasted content — no recording needed.
      </p>

      <div className="space-y-5">
        {/* Patient Selection */}
        <div>
          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Patient <span className="text-gray-400 dark:text-gray-600">(optional)</span></label>
          
          {selectedPatient ? (
            <div className="flex items-center gap-3 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg px-3 py-2.5">
              <User size={16} className="text-gray-400" />
              <span className="text-sm text-gray-900 dark:text-gray-100 font-medium">{patientLabel(selectedPatient)}</span>
              {selectedPatient.date_of_birth && (
                <span className="text-xs text-gray-400">· {selectedPatient.date_of_birth}</span>
              )}
              <button
                onClick={() => setSelectedPatient(null)}
                className="ml-auto text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                Change
              </button>
            </div>
          ) : (
            <div className="relative">
              <div className="relative">
                <Search size={16} className="absolute left-3 top-2.5 text-gray-400" />
                <input
                  type="text"
                  value={patientSearch}
                  onChange={(e) => { setPatientSearch(e.target.value); setShowPatientDropdown(true) }}
                  onFocus={() => setShowPatientDropdown(true)}
                  placeholder="Search patients..."
                  className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg pl-9 pr-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-brand-500"
                />
              </div>

              {showPatientDropdown && (
                <div className="absolute z-20 mt-1 w-full bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  <button
                    onClick={() => { setShowNewPatient(true); setShowPatientDropdown(false) }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-brand-600 dark:text-brand-400 hover:bg-gray-50 dark:hover:bg-gray-800"
                  >
                    <Plus size={14} />
                    New Patient
                  </button>
                  {filteredPatients.slice(0, 10).map(p => (
                    <button
                      key={p.id}
                      onClick={() => { setSelectedPatient(p); setShowPatientDropdown(false); setPatientSearch('') }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 text-left"
                    >
                      <User size={14} className="text-gray-400 shrink-0" />
                      <span>{patientLabel(p)}</span>
                      {p.date_of_birth && <span className="text-xs text-gray-400 ml-auto">· {p.date_of_birth}</span>}
                    </button>
                  ))}
                  {filteredPatients.length === 0 && !loadingPatients && (
                    <div className="px-3 py-2 text-sm text-gray-400">No patients found</div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Inline new patient form */}
          {showNewPatient && (
            <div className="mt-2 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg p-3 space-y-2">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newLastName}
                  onChange={(e) => setNewLastName(e.target.value)}
                  placeholder="Last name"
                  className="flex-1 bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg px-3 py-1.5 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-brand-500"
                  autoFocus
                />
                <input
                  type="text"
                  value={newFirstName}
                  onChange={(e) => setNewFirstName(e.target.value)}
                  placeholder="First name"
                  className="flex-1 bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg px-3 py-1.5 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-brand-500"
                />
              </div>
              <div className="flex gap-2 items-center">
                <input
                  type="date"
                  value={newDob}
                  onChange={(e) => setNewDob(e.target.value)}
                  className="flex-1 bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg px-3 py-1.5 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-brand-500"
                />
                <button
                  onClick={handleCreatePatient}
                  disabled={!newLastName.trim() || creatingPatient}
                  className="bg-brand-600 hover:bg-brand-500 disabled:opacity-50 text-white px-3 py-1.5 rounded-lg text-sm font-medium"
                >
                  {creatingPatient ? 'Creating...' : 'Add'}
                </button>
                <button
                  onClick={() => setShowNewPatient(false)}
                  className="text-gray-400 hover:text-gray-600 text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Note Type */}
        <div>
          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Note Type</label>
          <div className="grid grid-cols-3 gap-2">
            {NOTE_TYPES.map(t => {
              const locked = t.value === 'op_note' && tier !== 'pro'
              return (
              <button
                key={t.value}
                onClick={() => { if (!locked) setNoteType(t.value) }}
                title={locked ? 'Op notes require the Pro plan' : undefined}
                className={`w-full px-2 py-2 rounded-lg text-xs font-medium text-center transition-colors ${
                  locked
                    ? 'bg-gray-50 dark:bg-gray-900/50 text-gray-300 dark:text-gray-600 cursor-not-allowed'
                    : noteType === t.value
                    ? 'bg-brand-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-900 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-800'
                }`}
              >
                {t.label}{locked ? ' ✦' : ''}
              </button>
              )
            })}
          </div>
        </div>

        {/* Procedure Name (for op notes) */}
        {(noteType === 'office' || noteType === 'telehealth' || noteType === 'consult') && (
          <label className="flex items-center gap-2 mb-4 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={isNewPatient}
              onChange={(e) => setIsNewPatient(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 dark:border-gray-700 text-brand-600 focus:ring-brand-500"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">New patient</span>
            <span className="text-xs text-gray-400 dark:text-gray-500">(first visit to the practice — affects E/M coding)</span>
          </label>
        )}

        {(noteType === 'op_note' || noteType === 'procedure') && (
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Procedure Name <span className="text-gray-400 dark:text-gray-600">(optional)</span></label>
            <input
              type="text"
              value={procedureName}
              onChange={(e) => setProcedureName(e.target.value)}
              placeholder="e.g. Total Knee Arthroplasty, ACDF C5-6..."
              className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          </div>
        )}

        {/* Content */}
        <div>
          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">
            Dictation / Content
            <span className="font-normal text-gray-400 dark:text-gray-600 ml-1">— type, paste, or dictate what happened</span>
          </label>
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            rows={10}
            placeholder="Type or paste your dictation, clinical notes, or procedure details here. The AI will generate a structured note from this content..."
            className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg px-3 py-2.5 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-brand-500 resize-y leading-relaxed"
          />
          {content.length > 0 && <p className="text-xs text-gray-400 dark:text-gray-600 mt-1">{content.length.toLocaleString()} characters</p>}
        </div>

        {/* Generate Button */}
        <button
          onClick={handleGenerate}
          disabled={!content.trim() || generating}
          className="w-full flex items-center justify-center gap-2 bg-brand-600 hover:bg-brand-500 disabled:opacity-50 disabled:cursor-not-allowed text-white py-3 rounded-lg font-medium transition-colors"
        >
          {generating ? (
            <>
              <Loader2 size={18} className="animate-spin" />
              Creating visit...
            </>
          ) : (
            <>
              <FileText size={18} />
              Generate Note
            </>
          )}
        </button>
      </div>

      {/* Click-away handler for patient dropdown */}
      {showPatientDropdown && (
        <div className="fixed inset-0 z-10" onClick={() => setShowPatientDropdown(false)} />
      )}
    </div>
  )
}
